import { TabStyles } from '@tableau/widgets';
import { CSSProperties } from 'react';
import * as TypeStyle from 'typestyle';

import { CommonStyles } from '../../common/react/CommonStyles';
import { cssClass } from '../../style';

const { Colors, Sizing, Spacing, Typography } = TabStyles;

const cssPrefix = 'UserNotification';
export const userProfileSize = 38;
export const userSecondImageSize = 20;
export const dotSize = 8;
export const commonSpace = 16;
export const userProfileTextSize = 18;
export const errorActionRed = '#c93a47';
export const errorMsgTextPaddingLeft = 3 + dotSize + userProfileSize + commonSpace;

const notificationItem = cssClass(cssPrefix, 'notificationItem', {
  padding: commonSpace,
  outline: '1px solid transparent',
  border: '1px solid transparent',
  boxSizing: 'border-box',
  cursor: 'pointer',
  $nest: {
    '&:hover': {
      backgroundColor: '#ebebeb',
    },
    '&:focus': {
      outline: `1px solid ${CommonStyles.Colors.FocusBorderBlue}`,
      border: `1px solid transparent`,
      boxShadow: `0 0 4px 0 ${CommonStyles.Colors.FocusBorderBlue}`
    },
  }
});

const notificationContainer = cssClass(cssPrefix, 'notificationContainer', {
  display: 'flex',
  fontSize: '14px',
  minHeight: '95px',
  maxHeight: '285px',
});

// This class differs from the notificationItem class because it has no hover state
// It is used in the following scenarios:
// 1. Disabled notification: If there is no target url, the notification is not clickable
// 2. Loading notification: The class is used for the skeleton loader (no hover state)
const notificationItemDisabled = cssClass(cssPrefix, 'Disabled', {
  padding: commonSpace,
  outline: '1px solid transparent',
  border: '1px solid transparent',
  boxSizing: 'border-box',
  cursor: 'default',
  $nest: {
    '&:focus': {
      outline: `1px solid ${CommonStyles.Colors.FocusBorderBlue}`,
      border: `1px solid transparent`,
      boxShadow: `0 0 4px 0 ${CommonStyles.Colors.FocusBorderBlue}`
    },
  }
});

const transparent = cssClass(cssPrefix, 'transparent', {
  opacity: 0.3
});

const notificationError = cssClass(cssPrefix, 'notificationError', {
  color: errorActionRed,
  paddingTop: '2px',
  paddingRight: '8px',
  paddingLeft: errorMsgTextPaddingLeft,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.4,
  fontSize: '14px',
});

const notificationContentContainer: CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  paddingLeft: commonSpace
};

const contentHeader: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
};

const notificationTextContainerClass = cssClass(cssPrefix, 'notificationContainer', {
  $nest: {
    '&:focus': {
      outline: 'none'
    }
  }
});

const notificationFakeLink = cssClass(cssPrefix, 'notificationLink', {
  color: '#1a699e',
  $nest: {
    '&:hover': {
      textDecoration: 'underline'
    }
  }
});

const notificationTextContainer: CSSProperties = {
  marginTop: commonSpace,
  paddingRight: '8px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  lineHeight: 1.4,
};

const textWrap: CSSProperties = {
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  overflowWrap: 'break-word'
};

const notificationContent: CSSProperties = {
  ...notificationTextContainer,
  ...textWrap
};

const notificationTitle: CSSProperties = {
  ...notificationTextContainer,
  WebkitLineClamp: 2,
};

const createdAt: CSSProperties = {
  flexShrink: 0,
  fontSize: Typography.Sizing.Small,
  color: Typography.Color.Secondary
};

const profileContainer: CSSProperties = {
  display: 'flex'
};

const profileImageContainer: CSSProperties = {
  position: 'relative',
  height: userProfileSize,
  width: userProfileSize
};

function blueDotCss(showDot: boolean): CSSProperties {
  let background: string = showDot ? '#3498db' : 'transparent';
  return {
    width: dotSize,
    height: dotSize,
    marginTop: '15px',
    borderRadius: userProfileSize / 2,
    background: background,
    marginRight: Spacing.SmallSpace
  };
}

const actionsButton = cssClass(cssPrefix, 'actions-button', {
  position: 'absolute',
  right: '32px',
  top: 0,

  $nest: {
    'img:not(.notification-icon)': {
      display: 'none'
    },
    'button': {
      marginTop: '5px'
    },
    'button:focus': {
      outline: `1px solid ${CommonStyles.Colors.FocusBorderBlue} !important`,
      border: `1px solid transparent !important`,
      boxShadow: `0 0 4px 0 ${CommonStyles.Colors.FocusBorderBlue} !important`
    }
  }
});

const actionMenu: CSSProperties = {
  width: '100%',
  textAlign: 'left'
};

const notificationSectionTitle: CSSProperties = {
  paddingTop: commonSpace,
  paddingLeft: commonSpace
};

const notificationItemRow: CSSProperties = {
  position: 'relative',
  padding: '1px',
  display: 'flex'
};

function userProfileSecondImage(backgroundImage: string): CSSProperties {
  let userProfileImageCss: CSSProperties = {
    width: userSecondImageSize,
    height: userSecondImageSize,
    borderRadius: userSecondImageSize / 2,
    backgroundSize: '12px 12px',
    backgroundClip: 'content-box',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#222636',
    borderWidth: Sizing.BorderWidth,
    borderColor: Colors.White,
    borderStyle: 'solid',
    backgroundImage: `url(${backgroundImage})`,
    marginRight: Spacing.MediumSpace,
    boxSizing: 'content-box',
    position: 'absolute',
    bottom: '-4px',
    right: '-15px'
  };
  return userProfileImageCss;
}

/*
The following CSS classes are used for the NotificationLoader component:
shimmer, animate, loadingHeader, loadingText, loadingProfilePicture
*/
const shimmer = TypeStyle.keyframes({
  '0%': { backgroundPosition: '-500px 0' },
  '100%': { backgroundPosition: '500px 0' }
});

const animate = TypeStyle.style({
  animation: `${shimmer} 2s infinite linear`,
  background: 'linear-gradient(to right, #eff1f3 4%, #e2e2e2 25%, #eff1f3 36%)',
  backgroundSize: '1000px 100%'
});

const loadingHeader = TypeStyle.style({
  height: '15px',
  width: '275px',
  position: 'relative',
  borderRadius: '10px'
});

const loadingText = TypeStyle.style({
  height: '15px',
  width: '150px',
  position: 'relative',
  borderRadius: '10px'
});

const loadingProfilePicture = TypeStyle.style({
  height: userProfileSize,
  width: userProfileSize,
  borderRadius: '50%'
});

const loadingNotificationContainer = cssClass(cssPrefix, 'loadingNotificationContainer', {
  display: 'flex',
  padding: commonSpace,
  fontSize: '14px',
  minHeight: '0px',
  maxHeight: '160px',
});

export {
  userProfileSecondImage,
  profileImageContainer,
  notificationContainer,
  notificationItem,
  notificationContentContainer,
  contentHeader,
  notificationTitle,
  notificationContent,
  createdAt,
  blueDotCss,
  profileContainer,
  actionsButton,
  actionMenu,
  notificationItemRow,
  loadingProfilePicture,
  loadingHeader,
  loadingText,
  animate,
  loadingNotificationContainer,
  notificationSectionTitle,
  notificationTextContainerClass,
  notificationError,
  notificationItemDisabled,
  transparent,
  notificationFakeLink,
  textWrap
};